import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { Plane, Play, Square, Radio, AlertCircle, CheckCircle2, MapPin, Gauge, Activity } from 'lucide-react';
import { DroneTestMode } from '@/services/drone_integration/DroneTestMode';
import { ARAnalyzer, DamageDetectionResult } from '@/services/ARAnalyzer';
import { useRequestThrottle } from '@/hooks/useRequestThrottle';
import { useApiQuota } from '@/hooks/useApiQuota';
import { QuotaStatusBanner } from '@/components/QuotaStatusBanner';
import { QueueStatusIndicator } from '@/components/QueueStatusIndicator';
import { useAnalysisQueue } from '@/hooks/useAnalysisQueue';

type DroneType = 'DJI' | 'POTENSIC' | 'RTMP' | 'TEST';

interface DroneStatusData {
  connected: boolean;
  connectionType: string | null;
  model: string | null;
  gps: { lat: number; lng: number } | null;
  altitudeMeters: number | null;
  fps: number | null;
  framesAnalyzed: number;
  damageFindings: number;
  sessionId?: string;
  lastUpdated: string;
}

export default function DroneIntegration() {
  const [droneType, setDroneType] = useState<DroneType>('TEST');
  const [streamUrl, setStreamUrl] = useState('');
  const [status, setStatus] = useState<DroneStatusData | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [currentFrame, setCurrentFrame] = useState<string | null>(null);
  const [detectionResult, setDetectionResult] = useState<DamageDetectionResult | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  
  const testModeRef = useRef<DroneTestMode | null>(null);
  const statusIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const frameIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Request throttling: max 1 analysis every 3 seconds
  const { throttledRequest, getWaitTime, canMakeRequest } = useRequestThrottle({ 
    minInterval: 3000, // 3 seconds between requests
    maxConcurrent: 1 
  });
  
  // API quota tracking
  const { quotaStatus, checkQuota, incrementQuota } = useApiQuota('analysis');
  
  // Queue-based processing (scalable for production)
  const { submitJob, waitForJob } = useAnalysisQueue();
  const [useQueue, setUseQueue] = useState(true); // Default to queue mode

  useEffect(() => {
    // Poll status every 2 seconds when connected
    if (status?.connected) {
      statusIntervalRef.current = setInterval(fetchStatus, 2000);
    }

    return () => {
      if (statusIntervalRef.current) {
        clearInterval(statusIntervalRef.current);
      }
    };
  }, [status?.connected]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (testModeRef.current?.isRunning()) {
        testModeRef.current.stopTestFeed();
      }
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
      }
    };
  }, []);

  const fetchStatus = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('drone-status', {});
      if (error) {
        console.error('Status fetch error:', error);
        if (droneType !== 'TEST') {
          toast.error('Failed to fetch drone status');
        }
        return;
      }
      setStatus(data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      if (droneType !== 'TEST') {
        toast.error('Backend connection lost');
      }
    }
  };

  const handleConnect = async () => {
    setConnecting(true);

    try {
      const connectionOptions: any = {
        type: droneType === 'POTENSIC' ? 'RTMP' : droneType
      };

      if (droneType === 'RTMP' || droneType === 'POTENSIC') {
        if (!streamUrl) {
          toast.error('Stream URL is required');
          return;
        }
        connectionOptions.streamUrl = streamUrl;
      }

      if (droneType === 'DJI') {
        connectionOptions.deviceInfo = { model: 'DJI Phantom 4 Pro (Simulated)' };
      }

      if (droneType === 'POTENSIC') {
        connectionOptions.deviceInfo = { model: 'Potensic Atom SE' };
      }

      // For TEST mode, run simulation locally even if backend fails
      if (droneType === 'TEST') {
        try {
          const { data, error } = await supabase.functions.invoke('drone-connect', {
            body: connectionOptions
          });
          if (error) {
            console.warn('Backend logging unavailable, running local simulation:', error);
            toast.warning('Backend logging unavailable, running local simulation only');
          } else {
            toast.success(data.message || 'Test simulation started');
            await fetchStatus();
          }
        } catch (err) {
          console.warn('Backend unavailable, running local simulation:', err);
          toast.warning('Running local simulation only');
        }
        
        // Always start test feed in TEST mode
        startTestFeed();
      } else {
        // Normal drone connection
        const { data, error } = await supabase.functions.invoke('drone-connect', {
          body: connectionOptions
        });

        if (error) throw error;

        toast.success(data.message || 'Drone connected');
        await fetchStatus();
      }

    } catch (error) {
      console.error('Connection error:', error);
      toast.error(error instanceof Error ? error.message : 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      // Stop test feed if running
      if (testModeRef.current?.isRunning()) {
        testModeRef.current.stopTestFeed();
      }

      const { data, error } = await supabase.functions.invoke('drone-disconnect', {});
      if (error) throw error;

      toast.success('Drone disconnected');
      setStatus(null);
      setCurrentFrame(null);
      setDetectionResult(null);

    } catch (error) {
      console.error('Disconnection error:', error);
      toast.error('Failed to disconnect');
    }
  };

  const startTestFeed = () => {
    if (!testModeRef.current) {
      testModeRef.current = new DroneTestMode();
    }

    console.log('🚁 Starting test feed simulation...');
    
    testModeRef.current.startTestFeed(async (imageUrl: string) => {
      // Display the frame immediately
      setCurrentFrame(imageUrl);
      
      // Throttled analysis: only if quota allows and throttle permits
      if (!analyzing && canMakeRequest()) {
        const canProceed = await checkQuota();
        if (canProceed) {
          analyzeImageUrl(imageUrl);
        }
      }
    }, 2); // 2 FPS for better scalability

    toast.success('Live frame stream started at 2 FPS - Throttled AR analysis active');
  };

  const analyzeImageUrl = async (imageUrl: string) => {
    if (analyzing) return;
    
    setAnalyzing(true);
    try {
      if (useQueue) {
        // Queue mode: Submit job and poll for result (non-blocking)
        await throttledRequest(async () => {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(',')[1];
              resolve(base64Data);
            };
            reader.readAsDataURL(blob);
          });

          // Submit to queue
          const jobId = await submitJob(base64, {
            source: 'TEST',
            context: 'Drone test feed',
            priority: 5
          });

          if (jobId) {
            // Try to get result quickly (1 second timeout)
            try {
              const result = await waitForJob(jobId, { timeout: 1000, pollInterval: 200 });
              setDetectionResult(result);
              await incrementQuota();
            } catch (timeoutError) {
              // Job still processing in background - that's okay
              console.log('⏱️ Job queued for background processing');
            }
          }
        });
      } else {
        // Direct mode: Immediate analysis (higher latency, can timeout under load)
        await throttledRequest(async () => {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          
          const base64 = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const result = reader.result as string;
              const base64Data = result.split(',')[1];
              resolve(base64Data);
            };
            reader.readAsDataURL(blob);
          });

          const result = await ARAnalyzer.processBase64Image(base64, 'TEST');
          setDetectionResult(result);
          await incrementQuota();
        });
      }
    } catch (error) {
      console.error('Frame analysis error:', error);
      const waitTime = getWaitTime();
      if (waitTime > 0) {
        console.log(`⏱️ Next analysis available in ${(waitTime / 1000).toFixed(1)}s`);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Plane className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold">Drone Integration</h1>
            <p className="text-muted-foreground mt-1">
              Connect your drone or run in Test Mode to analyze roofs using AR damage detection
            </p>
          </div>
        </div>

        {/* Quota Status Banner */}
        <QuotaStatusBanner />

        {/* Processing Mode Toggle */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <QueueStatusIndicator />
                <div className="text-sm">
                  <span className="font-medium">Processing Mode: </span>
                  <span className="text-muted-foreground">
                    {useQueue ? 'Queue (Recommended for production)' : 'Direct (Development only)'}
                  </span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setUseQueue(!useQueue)}
                disabled={status?.connected}
              >
                Switch to {useQueue ? 'Direct' : 'Queue'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Connection Panel */}
        <Card>
          <CardHeader>
            <CardTitle>Drone Connection</CardTitle>
            <CardDescription>
              Select your drone type and connect to start real-time AR analysis
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Drone Type</label>
                <Select 
                  value={droneType} 
                  onValueChange={(v) => setDroneType(v as DroneType)}
                  disabled={status?.connected}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DJI">DJI Drone</SelectItem>
                    <SelectItem value="POTENSIC">Potensic Atom SE</SelectItem>
                    <SelectItem value="RTMP">Generic Drone (RTMP/HTTP Stream)</SelectItem>
                    <SelectItem value="TEST">🎬 Real-Time Test Feed (Video Simulation)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(droneType === 'RTMP' || droneType === 'POTENSIC') && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Stream URL</label>
                  <Input
                    value={streamUrl}
                    onChange={(e) => setStreamUrl(e.target.value)}
                    placeholder="rtmp://... or http://..."
                    disabled={status?.connected}
                  />
                </div>
              )}
            </div>

            {/* Connection Instructions */}
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              {droneType === 'DJI' && (
                <p>📱 Make sure your DJI drone is connected to this device via the DJI app, then click Connect.</p>
              )}
              {droneType === 'POTENSIC' && (
                <p>📡 Enter the RTMP stream URL from your Potensic Atom SE app and click Connect.</p>
              )}
              {droneType === 'RTMP' && (
                <p>📡 Enter your drone's RTMP or HTTP stream URL and click Connect.</p>
              )}
              {droneType === 'TEST' && (
                <p>🎬 Test Feed will stream aerial roof frames at 2 FPS with throttled AR damage detection (max 1 analysis every 3 seconds). No actual drone hardware required!</p>
              )}
            </div>

            {/* Connection Controls */}
            <div className="flex gap-2">
              {!status?.connected ? (
                <Button 
                  onClick={handleConnect}
                  disabled={connecting}
                  className="flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  {connecting ? 'Connecting...' : droneType === 'TEST' ? 'Start Test Feed (2 FPS Throttled)' : 'Connect'}
                </Button>
              ) : (
                <Button 
                  onClick={handleDisconnect}
                  variant="destructive"
                  className="flex items-center gap-2"
                >
                  <Square className="w-4 h-4" />
                  {droneType === 'TEST' ? 'Stop Live Feed Stream' : 'Disconnect'}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Live Preview + Status */}
        {(status?.connected || droneType === 'TEST') && (
          <div className="grid md:grid-cols-3 gap-6">
            {/* Live Preview */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Radio className="w-5 h-5 animate-pulse text-red-500" />
                  Live Feed + AR Detection
                  {droneType === 'TEST' && testModeRef.current?.isRunning() && (
                    <Badge className="ml-auto bg-green-500 text-white animate-pulse">
                      🎬 LIVE FRAME STREAM ACTIVE
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  {droneType === 'TEST' ? 'Throttled simulation at 2 FPS (max 1 analysis per 3 seconds)' : 'Live drone camera feed with damage detection overlay'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative bg-black rounded-lg overflow-hidden aspect-video">
                  {currentFrame ? (
                    <>
                      <img 
                        src={currentFrame} 
                        alt="Drone feed" 
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Damage Overlays */}
                      {detectionResult && detectionResult.annotations.length > 0 && (
                        <div className="absolute inset-0">
                          {detectionResult.annotations.map((ann, idx) => (
                            <div
                              key={ann.id}
                              className="absolute border-2 border-red-500 bg-red-500/20"
                              style={{
                                left: `${ann.bbox[0] * 100}%`,
                                top: `${ann.bbox[1] * 100}%`,
                                width: `${ann.bbox[2] * 100}%`,
                                height: `${ann.bbox[3] * 100}%`
                              }}
                            >
                              <div className="absolute -top-6 left-0 bg-red-500 text-white text-xs px-2 py-1 rounded">
                                {ann.type} ({(ann.confidence * 100).toFixed(0)}%)
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Analysis Indicator */}
                      {analyzing && (
                        <div className="absolute top-4 right-4 flex items-center gap-2 bg-primary/90 text-primary-foreground px-3 py-1 rounded-full">
                          <Activity className="w-4 h-4 animate-spin" />
                          <span className="text-sm font-medium">Analyzing...</span>
                        </div>
                      )}
                      
                      {/* Quota Status Overlay */}
                      {quotaStatus && (
                        <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm px-3 py-2 rounded-lg text-xs space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">API Quota:</span>
                            <span className="font-medium">{quotaStatus.requestsUsed}/{quotaStatus.requestsLimit}</span>
                          </div>
                          {quotaStatus.percentUsed >= 80 && (
                            <div className="text-amber-600 dark:text-amber-400">
                              ⚠️ {(100 - quotaStatus.percentUsed).toFixed(0)}% remaining
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      <p>Waiting for frames...</p>
                    </div>
                  )}
                </div>

                {/* Detection Summary */}
                {detectionResult && (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    <div className="p-3 bg-muted/50 rounded-lg text-center">
                      <p className="text-2xl font-bold">{detectionResult.summary.totalFindings}</p>
                      <p className="text-xs text-muted-foreground">Total Findings</p>
                    </div>
                    <div className="p-3 bg-orange-500/10 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-600">{detectionResult.summary.hailCount}</p>
                      <p className="text-xs text-muted-foreground">Hail Detected</p>
                    </div>
                    <div className="p-3 bg-red-500/10 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-600">{detectionResult.summary.otherDamageCount}</p>
                      <p className="text-xs text-muted-foreground">Other Damage</p>
                    </div>
                  </div>
                )}

                {/* Raw Analysis */}
                {detectionResult?.rawAnalysis && (
                  <div className="mt-4 p-4 bg-muted/30 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{detectionResult.rawAnalysis.damage_type}</span>
                      <Badge variant={detectionResult.rawAnalysis.confidence_score > 0.8 ? 'default' : 'secondary'}>
                        {(detectionResult.rawAnalysis.confidence_score * 100).toFixed(0)}% confidence
                      </Badge>
                    </div>
                    {detectionResult.rawAnalysis.linear_measurement_ft > 0 && (
                      <p className="text-sm text-muted-foreground">
                        Linear measurement: {detectionResult.rawAnalysis.linear_measurement_ft.toFixed(1)} ft
                      </p>
                    )}
                    {detectionResult.rawAnalysis.compliance_alert && (
                      <p className="text-sm text-amber-600 dark:text-amber-400">
                        ⚠️ {detectionResult.rawAnalysis.compliance_alert}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Panel */}
            <Card>
              <CardHeader>
                <CardTitle>Connection Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Connection Status */}
                <div className="flex items-center gap-2">
                  {status?.connected ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                  <span className="font-medium">
                    {status?.connected ? 'Connected' : droneType === 'TEST' ? 'Local Simulation' : 'Not Connected'}
                  </span>
                </div>

                {status && (
                  <>
                    {/* Drone Type */}
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Type</p>
                      <p className="font-medium">{status.connectionType || droneType}</p>
                    </div>

                    {/* Model */}
                    {status.model && (
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Model</p>
                        <p className="font-medium text-sm">{status.model}</p>
                      </div>
                    )}

                    {/* GPS */}
                    {status.gps && (
                      <div className="space-y-1 flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">GPS</p>
                          <p className="text-xs font-mono">
                            {status.gps.lat.toFixed(6)}, {status.gps.lng.toFixed(6)}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Altitude */}
                    {status.altitudeMeters !== null && status.altitudeMeters !== undefined && (
                      <div className="space-y-1 flex items-start gap-2">
                        <Gauge className="w-4 h-4 text-primary mt-0.5" />
                        <div>
                          <p className="text-sm text-muted-foreground">Altitude</p>
                          <p className="text-sm font-medium">{status.altitudeMeters.toFixed(1)}m</p>
                        </div>
                      </div>
                    )}

                    {/* FPS */}
                    {status.fps && (
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">FPS</p>
                        <p className="font-medium">{status.fps.toFixed(1)}</p>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="pt-4 border-t space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Frames Analyzed</span>
                        <Badge variant="secondary">{status.framesAnalyzed}</Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Damage Findings</span>
                        <Badge variant={status.damageFindings > 0 ? 'destructive' : 'secondary'}>
                          {status.damageFindings}
                        </Badge>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        {!status?.connected && (
          <Card className="border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-primary mt-0.5" />
                <div className="space-y-2">
                  <p className="font-semibold">How it works:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Select your drone type or Test Mode</li>
                    <li>Connect to start receiving video frames</li>
                    <li>AI analyzes each frame for damage detection</li>
                    <li>See real-time AR overlays with damage markers</li>
                    <li>All findings are logged and can be saved to jobs</li>
                  </ul>
                  <p className="text-sm mt-3">
                    <strong>Test Mode</strong> works 100% without real drone hardware - perfect for demos and testing!
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
